import { Box, Tab, Tabs } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import UnsavedChangesDialog from "../shared/UnsavedChangesDialog";
import { CustomTabPanel, a11yProps } from "./tabPanelUtils";
import AccountingReportsSubTab from "./AccountingReportsSubTab";
import AccountingIntegrationSubTab from "./AccountingIntegrationSubTab";

// Top-level "Kirjanpito" (Accounting) settings tab.
// Renders a nested sub-tab layout: "Raportit" with the accountant email /
// monthly report settings (the former content of this file, now in
// AccountingReportsSubTab) and "Integraatio" with the accounting software
// integrations (Netvisor etc.). Dirty state from the active sub-tab is bubbled
// up to MainSettingsView via onDirtyChange so the top-level tab-switch /
// route-leave guards keep working (mirrors OrganizationFeaturesTab).
//
// The Integraatio sub-tab is visible to everyone who can see the Kirjanpito tab
// (MainSettingsView already limits that to foreman and admin). Which provider
// CARDS render is gated per provider inside AccountingIntegrationSubTab.
function OrganizationAccountingTab({ t, onDirtyChange }) {
  const [subValue, setSubValue] = useState(0);

  // Sub-tab switch guard (mirrors MainSettingsView's top-level guard)
  const subTabDirtyRef = useRef(false);
  const subSaveFnRef = useRef(null);
  const [pendingSubTab, setPendingSubTab] = useState(null);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [subSwitchSaving, setSubSwitchSaving] = useState(false);

  // The active sub-tab reports its dirty/save here; we store it and re-bubble to the parent.
  const handleSubDirtyChange = useCallback(
    (dirty, saveFn) => {
      subTabDirtyRef.current = dirty;
      subSaveFnRef.current = saveFn;
      onDirtyChange?.(dirty, saveFn);
    },
    [onDirtyChange]
  );

  const subTabs = [
    {
      key: "reports",
      label: t("accounting.reportsTab", "Raportit"),
      render: (handleDirty) => <AccountingReportsSubTab onDirtyChange={handleDirty} />,
    },
    {
      key: "integration",
      label: t("accounting.integrationTab", "Integraatio"),
      // The integration sub-tab saves via its own explicit buttons and never
      // reports dirty state (like TimeclockSettingsTab in the features tab).
      render: () => <AccountingIntegrationSubTab />,
    },
  ];

  const doSwitchSubTab = useCallback(
    (newValue) => {
      // Clear stale dirty before mounting the next sub-tab. The newly mounted
      // reports tab re-asserts its own state on mount; the integration tab stays clean.
      subTabDirtyRef.current = false;
      subSaveFnRef.current = null;
      onDirtyChange?.(false, null);
      setSubValue(newValue);
    },
    [onDirtyChange]
  );

  const handleSubChange = (event, newValue) => {
    if (subTabDirtyRef.current) {
      setPendingSubTab(newValue);
      setShowSubDialog(true);
    } else {
      doSwitchSubTab(newValue);
    }
  };

  const handleSubSwitchSave = useCallback(async () => {
    setSubSwitchSaving(true);
    try {
      if (subSaveFnRef.current) {
        await subSaveFnRef.current();
      }
      setShowSubDialog(false);
      if (pendingSubTab !== null) {
        const tab = pendingSubTab;
        setPendingSubTab(null);
        setTimeout(() => doSwitchSubTab(tab), 0);
      }
    } catch (err) {
      // Save failed — stay on current sub-tab
    } finally {
      setSubSwitchSaving(false);
    }
  }, [pendingSubTab, doSwitchSubTab]);

  const handleSubSwitchDiscard = useCallback(() => {
    setShowSubDialog(false);
    if (pendingSubTab !== null) {
      const tab = pendingSubTab;
      setPendingSubTab(null);
      setTimeout(() => doSwitchSubTab(tab), 0);
    }
  }, [pendingSubTab, doSwitchSubTab]);

  const handleSubSwitchCancel = useCallback(() => {
    setShowSubDialog(false);
    setPendingSubTab(null);
  }, []);

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={subValue}
          onChange={handleSubChange}
          aria-label="accounting settings tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              minWidth: { xs: "auto", sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: "13px", sm: "14px" },
            },
          }}
        >
          {subTabs.map((tab, index) => (
            <Tab key={tab.key} label={tab.label} {...a11yProps(index, "accounting")} />
          ))}
        </Tabs>
      </Box>

      {subTabs.map((tab, index) => (
        <CustomTabPanel key={tab.key} value={subValue} index={index} idPrefix="accounting">
          {tab.render(handleSubDirtyChange)}
        </CustomTabPanel>
      ))}

      <UnsavedChangesDialog
        open={showSubDialog}
        onSave={handleSubSwitchSave}
        onDiscard={handleSubSwitchDiscard}
        onCancel={handleSubSwitchCancel}
        saving={subSwitchSaving}
      />
    </Box>
  );
}

export default withTranslation()(OrganizationAccountingTab);
